import { Alert, Classes } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";

import {
  isCoach,
  isInWorkspace,
} from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { RescheduleBalanceNotEnoughErrModal } from "@/components/pages_logged_in/schedule/reschedule/RescheduleBalanceNotEnough";
import { RescheduleStatus } from "@/graphql";
import { DetailQueryMeeting } from "@/types/meetings";

function getBalanceErrorMessage() {
  return isCoach()
    ? "Customer currently don't have enough credits to reschedule, please contact them to purchase extra credits or you will need to reschedule withoutcharging the customer."
    : !isInWorkspace()
      ? "You don't have enough credits to reschedule, please purchase extra credits"
      : "You don't have enough credits to reschedule, please request your workspace to provide you with more credits.";
}

const errorMessageMap: Partial<Record<RescheduleStatus | "default", string>> = {
  [RescheduleStatus.WorkspaceBalanceNotEnough]:
    "Whoops! It looks like there is an issue with your workspace's billing. Please contact your workspace admin and let them know the account is out of credits.",
  [RescheduleStatus.NotCompleted]:
    "Reschedule not completed yet. Please refresh and try again",
  [RescheduleStatus.WrongWorkspace]:
    "This meeting was previously scheduled in a different workspace, Please switch your workspace.",
  [RescheduleStatus.MeetingOverlap]:
    "Unfortunately, the timeslot you selected is no longer available. Please refresh and select another time.",
  [RescheduleStatus.AttendeesScheduleBlock]:
    "Unfortunately, an attendees scheduled invite is currently planned to run after your new timeslot. Please refresh and select another time or adjust the scheduled attendees invite",
  default: "Reschedule Fail. Please contact support",
};

function getOtherErrorMessage(error: RescheduleStatus) {
  return errorMessageMap[error] || errorMessageMap["default"];
}

interface ErrorModalContainerProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  error: RescheduleStatus;
  meeting?: DetailQueryMeeting | null;
}

export function ErrorModal({
  visible,
  onCancel,
  onConfirm,
  error,
  meeting = null,
}: ErrorModalContainerProps) {
  const navigate = useNavigate();
  const isBalanceNotEnough = error === RescheduleStatus.BalanceNotEnough;

  const errorMsg = isBalanceNotEnough
    ? getBalanceErrorMessage()
    : getOtherErrorMessage(error);

  const isBuyCreditRedirect =
    !isCoach() && !isInWorkspace() && isBalanceNotEnough;

  if (isBalanceNotEnough) {
    return (
      <RescheduleBalanceNotEnoughErrModal
        visible={visible}
        onCancel={onCancel}
        isBuyCreditRedirect={isBuyCreditRedirect}
        meeting={meeting}
      />
    );
  }

  return (
    <Alert
      isOpen={visible}
      intent="danger"
      cancelButtonText={isBuyCreditRedirect ? "Close" : ""}
      confirmButtonText={isBuyCreditRedirect ? "Go to Buy Credits" : "Close"}
      style={{ width: "480px" }}
      onCancel={onCancel}
      onConfirm={() => (isBuyCreditRedirect ? navigate("/buy") : onConfirm())}
    >
      <StyledContainerList
        py={16}
        px={20}
        backgroundColor="#F6F6FB"
        fontSize="16px"
      >
        {errorMsg}
      </StyledContainerList>
    </Alert>
  );
}
const StyledContainerList = styled(Box)`
  .${Classes.BUTTON} {
    padding: 12px 19px;
    margin-right: 10px;
  }
  .${Classes.BUTTON_TEXT} {
    font-size: 16px;
  }

  .${Classes.FORM_GROUP} {
    margin: 0;
  }

  border-radius: 12px;
`;
